import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import type { ApiError } from '@/core/api';
import { businessDaysBetween } from '@/lib/datetime';
import { LEAVE_TYPES } from '@/features/hrm/time-off';
import type { CabinetTimeOffInput } from '../types';

const schema = z
  .object({
    type: z.enum(['vacation', 'sick', 'unpaid', 'other']),
    start_date: z.string().min(1, 'Required'),
    end_date: z.string().min(1, 'Required'),
    days: z.string().min(1, 'Required'),
    reason: z.string().max(255),
  })
  .refine((v) => !v.start_date || !v.end_date || v.end_date >= v.start_date, {
    path: ['end_date'],
    message: 'End date can’t be before the start date.',
  });

type FormValues = z.infer<typeof schema>;

const FIELD_KEYS = ['type', 'start_date', 'end_date', 'days', 'reason'] as const;

interface CabinetTimeOffFormProps {
  defaultValues?: Partial<FormValues>;
  submitLabel: string;
  onSubmit: (input: CabinetTimeOffInput) => Promise<unknown>;
  onCancel: () => void;
}

export function CabinetTimeOffForm({
  defaultValues,
  submitLabel,
  onSubmit,
  onCancel,
}: CabinetTimeOffFormProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      type: 'vacation',
      start_date: '',
      end_date: '',
      days: '',
      reason: '',
      ...defaultValues,
    },
  });


  // Auto-fill days as the inclusive Mon–Fri count whenever the range changes;
  // the field stays editable for half-days / org-specific adjustments.
  const startDate = useWatch({ control: form.control, name: 'start_date' });
  const endDate = useWatch({ control: form.control, name: 'end_date' });
  useEffect(() => {
    if (!startDate || !endDate) return;
    const days = businessDaysBetween(startDate, endDate);
    if (days > 0) form.setValue('days', String(days), { shouldValidate: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- form is stable
  }, [startDate, endDate]);

  async function handleSubmit(values: FormValues) {
    try {
      await onSubmit(values);
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          const key = FIELD_KEYS.find((k) => k === field);
          form.setError(key ?? 'root', { message: messages.join(' ') });
        }
      }
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Type</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {LEAVE_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="days"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Days</FormLabel>
                <FormControl>
                  <Input type="number" min="0" step="0.5" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="start_date"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Start date</FormLabel>
                <FormControl>
                  <Input type="date" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="end_date"
            render={({ field }) => (
              <FormItem>
                <FormLabel>End date</FormLabel>
                <FormControl>
                  <Input type="date" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="reason"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Reason</FormLabel>
              <FormControl>
                <Textarea rows={2} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {form.formState.errors.root ? (
          <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
        ) : null}

        <div className="flex gap-2">
          <Button type="submit" disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting ? 'Saving…' : submitLabel}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Form>
  );
}
